import styled from '@emotion/styled';
import Layout from '@ep/components/Layout';
import SEO from '@ep/components/Seo';
import CTAModule, { CTAModuleProps } from '@ep/modules/CTAModule';
import HeroVideoModule, {
  HeroVideoModuleProps,
} from '@ep/modules/HeroVideoModule';
import MediaModule from '@ep/modules/NewMediaModule';
import RelatedProductsModule from '@ep/modules/RelatedProductsModule';
import SavingsToolModule from '@ep/modules/SavingsToolModule';
import SectionMenu, { SectionMenuProps } from '@ep/modules/SectionMenuModule';
import StatsModule from '@ep/modules/StatsModule';
import TabCarouselModule, {
  TabCarouselModuleProps,
} from '@ep/modules/TabCarouselModule';
import TestimonialModule from '@ep/modules/TestimonialModule';
import VideoGridModule, { VideoGridProp } from '@ep/modules/VideoGridModule';
import theme from '@ep/theme';
import { Gcms_ProductPage } from '@ep/types/gatsbyTypes';
import getModuleProps from '@ep/utils/getModuleProps';
import getSeoProps from '@ep/utils/getSeoProps';
import { WindowLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React from 'react';

export interface ProductPageProps {
  location: WindowLocation;
  data: {
    gcms: {
      productPage: Gcms_ProductPage;
    };
  };
}

// --------------------------------------------------
// START STYLE OVERRIDE
// THIS SHOULD BE REMOVED ONCE THE STYLE IS APPLIED GLOBALLY
// --------------------------------------------------

export const H1 = styled.h1`
  font-family: ${theme.typography.semiBoldFont};
  font-size: ${theme.typography.toRem(42)};
  line-height: 48px;
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(32)};
    line-height: 38px;
  }
`;

export const H2 = styled.h2`
  font-family: ${theme.typography.semiBoldFont};
  font-size: ${theme.typography.toRem(30)};
  line-height: 30px;
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(26)};
    line-height: 26px;
  }
`;

export const H3 = styled.h3`
  font-family: ${theme.typography.boldFont};
  font-size: ${theme.typography.toRem(20)};
  line-height: 20px;
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(14)};
    line-height: 14px;
  }
`;

export const Label = styled.h3`
  font-family: ${theme.typography.semiBoldFont};
  font-size: ${theme.typography.toRem(30)};
  color: ${theme.colors.duskBlue};
`;

export const Body = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.toRem(15)};
  line-height: 24px;
`;

const DisclaimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  ${theme.mediaQueries.mobile} {
    flex-direction: row;
  }
`;
const DisclaimerContent = styled.p`
  align-self: center;
  padding: 0 2rem;
  font-size: 0.9375rem;
  width: 45%;
  text-align: center;
  ${theme.mediaQueries.mobile} {
    width: 100%;
    padding: 0 1rem;
  }
`;

// --------------------------------------------------
// END STYLE OVERRIDE
// --------------------------------------------------

function ProductPage({ location, data }: ProductPageProps) {
  const page = data.gcms.productPage;
  const hasSectionMenu = Boolean(
    page.subMenu ? page.subMenu.length > 0 : false,
  );
  const hasHeroSection = Boolean(page.heroLabel);
  const hasTabsSection = Boolean(page.tabPosts?.length);
  const hasStatsSection = Boolean(page.statPosts?.length);
  const hasRelatedProductsSection = Boolean(page.relatedProducts?.length);
  const hasTopFeatures = Boolean(page.topFeatures?.length);
  const hasVideoGridModule = Boolean(page.videoModuleBackground);
  const hasTopTestimonialSection = Boolean(page.topTestimonials?.length);
  const hasTestimonialSection = Boolean(page.testimonials?.length);
  const hasSavingsTool = Boolean(
    page.savingsTool ? page.savingsTool.length > 0 : false,
  );
  const hasBottomFeatures = Boolean(page.bottomFeatures?.length);
  const hasCTASection = Boolean(page.ctaButtonLabel);

  // check if the disclaimer is exist or not
  const hasDisclaimer = Boolean(page.savingsDisclaimer);
  return (
    <Layout pathname={location.pathname} subMenu={page.subMenu}>
      <SEO {...getSeoProps(page)} />
      {hasSectionMenu && (
        <SectionMenu
          pathname={location.pathname}
          {...(getModuleProps(page, 'sub') as SectionMenuProps)}
        />
      )}
      {hasHeroSection && (
        <HeroVideoModule
          {...(getModuleProps(page, 'hero') as HeroVideoModuleProps)}
        />
      )}
      {hasTopTestimonialSection && (
        <TestimonialModule testimonials={page.topTestimonials} />
      )}

      {hasTabsSection && (
        <TabCarouselModule
          {...(getModuleProps(page, 'tabs') as TabCarouselModuleProps)}
          tabHeading={page.tabHeading}
          tabs={page.tabPosts}
        />
      )}
      {hasStatsSection && (
        <StatsModule
          stats={page.statPosts}
          smartTimeProofPoint={page.smartTimeProofPoint}
        />
      )}
      {hasVideoGridModule && (
        <VideoGridModule
          {...(getModuleProps(page, 'videoModule') as VideoGridProp)}
        />
      )}
      {hasTopFeatures && (
        <>
          {page.topFeatures?.map(
            (
              feature: {
                id: React.Key;
                heading: string;
                content: string;
                image: string;
                buttonLabelText: string;
                buttonLabelLink: string;
                featureExpand: boolean;
                expandLinkText: string;
                collapseLinkText: string;
                lightboxText: string;
                lightboxImage: string;
                imageCheckboxes: string[];
              },
              index: number,
            ) => (
              <MediaModule
                key={feature.id}
                heading={feature.heading}
                bodyContent={feature.content}
                media={feature.image}
                right={hasTabsSection ? index % 2 === 0 : index % 2 !== 0}
                noPaddingTop={index !== 0}
                bottom
                buttonLabel={feature.buttonLabelText}
                buttonLink={feature.buttonLabelLink}
                textExpand={feature.featureExpand}
                expandLinkText={feature.expandLinkText}
                collapseLinkText={feature.collapseLinkText}
                lightboxText={feature.lightboxText}
                lightboxImage={feature.lightboxImage}
                imageCheckboxes={feature.imageCheckboxes}
              />
            ),
          )}
        </>
      )}
      {hasTestimonialSection && (
        <TestimonialModule testimonials={page.testimonials} />
      )}
      {hasSavingsTool && (
        <SavingsToolModule
          savingsToolData={page.savingsTool}
          savingsDisclaimer={page.savingsDisclaimer}
        />
      )}
      {hasBottomFeatures && (
        <>
          {page.bottomFeatures?.map(
            (
              feature: {
                id: React.Key;
                heading: string;
                content: string;
                image: string;
                buttonLabelText: string;
                buttonLabelLink: string;
                featureExpand: boolean;
                expandLinkText: string;
                collapseLinkText: string;
                lightboxText: string;
                lightboxImage: string;
                imageCheckboxes: string[];
              },
              index: number,
            ) => (
              <MediaModule
                key={feature.id}
                heading={feature.heading}
                bodyContent={feature.content}
                media={feature.image}
                right={hasTabsSection ? index % 2 !== 0 : index % 2 === 0}
                noPaddingTop={index !== 0}
                bottom
                buttonLabel={feature.buttonLabelText}
                buttonLink={feature.buttonLabelLink}
                textExpand={feature.featureExpand}
                expandLinkText={feature.expandLinkText}
                collapseLinkText={feature.collapseLinkText}
                lightboxText={feature.lightboxText}
                lightboxImage={feature.lightboxImage}
                imageCheckboxes={feature.imageCheckboxes}
              />
            ),
          )}
        </>
      )}
      {/* Desclaimer Section */}
      {hasDisclaimer && (
        <DisclaimerContainer>
          <DisclaimerContent>{page.pageDisclaimer}</DisclaimerContent>
        </DisclaimerContainer>
      )}
      {hasRelatedProductsSection && (
        <RelatedProductsModule
          heading={page.relatedProductsHeading}
          relatedProducts={page.relatedProducts}
        />
      )}
      {hasCTASection && (
        <CTAModule
          {...(getModuleProps(page, 'cta') as CTAModuleProps)}
          className={'narrowWidth'}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query ProductPageQuery($id: ID!) {
    gcms {
      productPage(where: { id: $id }) {
        title
        metaTitle
        websiteType
        description
        websiteType
        metaDescription
        structuredData
        slug
        featuredImage {
          url
        }
        subMenu {
          data
        }
        heroImage {
          url
          altText
          vidyardLink
          options
        }
        heroVideo {
          url
          altText
          vidyardLink
          options
        }
        heroVideoTitle
        heroVideoText
        heroLabel
        heroHeading
        heroSubheading
        heroButtonLabel
        heroButtonLink
        heroUnstyledButtonLabel
        heroUnstyledButtonLink
        tabHeading
        tabPosts {
          id
          label
          heading
          description {
            html
          }
          image {
            url
            altText
            vidyardLink
          }
          soundFile {
            url
          }
          mobileImage {
            url
            altText
            vidyardLink
          }
        }
        statPosts {
          id
          value
          description {
            html
          }
          prefixUnit
          initialStat
          unit
          unitSuffix
          updateValue
          updatedAt
        }
        smartTimeProofPoint
        savingsDisclaimer
        pageDisclaimer
        topFeatures {
          id
          image {
            url
            altText
            vidyardLink
            options
          }
          imageCheckboxes
          heading
          content {
            html
          }
          lightboxImage {
            url
            altText
            vidyardLink
            options
          }
          featureExpand
          expandLinkText
          collapseLinkText
          lightboxText {
            html
          }
          buttonLabelText
          buttonLabelLink
          lightboxImage {
            url
            altText
            vidyardLink
            options
          }
          lightboxText {
            html
          }
        }
        videoModuleTitle
        videoModuleBackground {
          url
          altText
          vidyardLink
        }
        videoModuleItems {
          title
          showTitle
          media {
            url
            altText
            vidyardLink
            options
          }
          pinSize
          pinColor {
            hex
          }
          mediaCaptionContent {
            html
          }
          mediaCaption
          xCoordinate
          yCoordinate
        }
        topTestimonials {
          id
          quote
          author
          jobTitle
          productionName
          media {
            url
            altText
            vidyardLink
          }
        }
        testimonials {
          id
          quote
          author
          jobTitle
          productionName
          media {
            url
            altText
            vidyardLink
          }
        }
        savingsTool
        bottomFeatures {
          id
          image {
            url
            altText
            vidyardLink
            options
          }
          imageCheckboxes
          heading
          content {
            html
          }
          lightboxImage {
            url
            altText
            vidyardLink
            options
          }
          featureExpand
          expandLinkText
          collapseLinkText
          lightboxText {
            html
          }
          buttonLabelText
          buttonLabelLink
          lightboxImage {
            url
            altText
            vidyardLink
            options
          }
          lightboxText {
            html
          }
        }
        savingsDisclaimer
        relatedProductsHeading
        relatedProducts {
          name
          link
          image {
            url
            altText
            vidyardLink
            options
          }
        }
        ctaTextColor {
          hex
        }
        ctaHeading
        ctaHeadingColor {
          hex
        }
        ctaSubheadingColor {
          hex
        }
        ctaSecondarySubheadingColor {
          hex
        }
        ctaSubheading {
          html
        }
        ctaButtonLabel
        ctaButtonLink
        ctaBackground {
          altText
          url
        }
        ctaSecondarySubheading {
          html
        }
      }
    }
  }
`;

export default ProductPage;
